<template>
    <div
        class="flex items-center justify-between py-3 w-full"
        aria-label="Pagination"
    >
        <!-- mobile (boxed variant): centered numbered pager, count below -->
        <div
            v-if="isBoxed"
            class="flex flex-1 flex-col items-center gap-2 sm:hidden"
        >
            <nav
                v-if="totalPages > 1"
                class="flex items-center space-x-1"
                aria-label="Pagination"
            >
                <button
                    @click="prev"
                    :disabled="isInFirstPage"
                    :class="arrowBtnClass"
                >
                    <span class="sr-only">Previous</span>
                    <Icon icon="chevron-right" svgClass="w-4 h-4 rotate-180" />
                </button>
                <template v-if="mobileWindow.showFirst">
                    <button
                        @click="() => goToPage(1)"
                        :class="[pageBtnBaseClass, pageBtnStateClass(currentPage === 1)]"
                    >
                        1
                    </button>
                    <span
                        v-if="mobileWindow.showLeadingEllipsis"
                        :class="ellipsisClass"
                        >…</span
                    >
                </template>
                <button
                    v-for="page in mobileWindow.pages"
                    :key="page.name"
                    @click="() => goToPage(page.name)"
                    aria-current="page"
                    :class="[pageBtnBaseClass, pageBtnStateClass(Number(page.name) === currentPage)]"
                >
                    {{ page.name }}
                </button>
                <template v-if="mobileWindow.showLast">
                    <span
                        v-if="mobileWindow.showTrailingEllipsis"
                        :class="ellipsisClass"
                        >…</span
                    >
                    <button
                        @click="() => goToPage(lastPageNumber)"
                        :class="[pageBtnBaseClass, pageBtnStateClass(currentPage === lastPageNumber)]"
                    >
                        {{ lastPageNumber }}
                    </button>
                </template>
                <button
                    @click="next"
                    :disabled="isInLastPage"
                    :class="arrowBtnClass"
                >
                    <span class="sr-only">Next</span>
                    <Icon icon="chevron-right" svgClass="w-4 h-4" />
                </button>
            </nav>
            <ResultsCount
                :pClass="countPClass"
                :startRange="startRange"
                :endRange="endRange"
                :totalResults="totalResults"
                :noun="noun"
                :separator="separator"
            />
        </div>
        <!-- mobile (default variant): count left, prev/next right -->
        <div v-else class="flex flex-1 justify-between sm:hidden">
            <div class="flex flex-col space-y-2">
                <ResultsCount
                    :pClass="countPClass"
                    :startRange="startRange"
                    :endRange="endRange"
                    :totalResults="totalResults"
                    :noun="noun"
                    :separator="separator"
                />
            </div>
            <div class="flex items-center space-x-2">
                <button
                    v-if="totalPages > 1"
                    @click="prev"
                    :disabled="isInFirstPage"
                    class="relative inline-flex items-center rounded-md border border-gray-300 dark:border-slate-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                >
                    Previous
                </button>
                <button
                    v-if="totalPages > 1"
                    @click="next"
                    :disabled="isInLastPage"
                    class="relative inline-flex items-center rounded-md border border-gray-300 dark:border-slate-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                >
                    Next
                </button>
            </div>
        </div>
        <!-- desktop: -->
        <div
            class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between"
        >
            <div class="flex items-center space-x-4">
                <ResultsCount
                    :pClass="countPClass"
                    :startRange="startRange"
                    :endRange="endRange"
                    :totalResults="totalResults"
                    :noun="noun"
                    :separator="separator"
                />
            </div>
            <nav
                v-if="totalPages > 1"
                class="flex justify-start items-start rounded text-slate-800 dark:text-gray-100 font-normal text-sm space-x-1"
                aria-label="Pagination"
            >
                <button
                    @click="prev"
                    :disabled="isInFirstPage"
                    :class="arrowBtnClass"
                >
                    <span class="sr-only">Previous</span>
                    <Icon icon="chevron-right" svgClass="w-4 h-4 rotate-180" />
                </button>
                <!-- Leading edge: first page + ellipsis -->
                <template v-if="showLeadingEdge">
                    <button
                        @click="() => goToPage(1)"
                        :class="[pageBtnBaseClass, pageBtnStateClass(currentPage === 1)]"
                    >
                        1
                    </button>
                    <span
                        :class="ellipsisClass"
                        >…</span
                    >
                </template>
                <button
                    v-for="page in pages"
                    :key="page.name"
                    @click="() => goToPage(page.name)"
                    aria-current="page"
                    :class="[pageBtnBaseClass, pageBtnStateClass(Number(page.name) === currentPage)]"
                >
                    {{ page.name }}
                </button>
                <!-- Trailing edge: ellipsis + last page -->
                <template v-if="showTrailingEdge">
                    <span
                        :class="ellipsisClass"
                        >…</span
                    >
                    <button
                        @click="() => goToPage(lastPageNumber)"
                        :class="[pageBtnBaseClass, pageBtnStateClass(currentPage === lastPageNumber)]"
                    >
                        {{ lastPageNumber }}
                    </button>
                </template>
                <button
                    @click="next"
                    :disabled="isInLastPage"
                    :class="arrowBtnClass"
                >
                    <span class="sr-only">Next</span>
                    <Icon icon="chevron-right" svgClass="w-4 h-4" />
                </button>
            </nav>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import ResultsCount from '@/components/ui/results-count/results-count.vue';

const props = defineProps({
    totalResults: {
        type: Number,
        required: true,
    },
    totalPages: {
        type: Number,
        required: true,
    },
    perPage: {
        type: Number,
        required: true,
    },
    currentPage: {
        type: Number,
        required: true,
        default: 1,
    },
    maxVisibleButtons: {
        type: Number,
        default: 5,
    },
    // Noun used by the embedded results count, e.g. "transactions".
    noun: {
        type: String,
        default: 'results',
    },
    // Range separator for the results count: "to" => "1 to 50", "-" => "1-50".
    separator: {
        type: String,
        default: 'to',
    },
    // When true, always show the first/last page with an ellipsis when the
    // visible window doesn't already reach them (e.g. "1 2 3 4 5 … 17").
    showEdges: {
        type: Boolean,
        default: false,
    },
    // Visual variant. 'default' keeps the classic style; 'boxed' renders the
    // Design System v2 pager (square buttons, dark filled current page).
    variant: {
        type: String,
        default: 'default',
    },
});

const emit = defineEmits(['pageChanged']);

// Variant-driven class sets (v2 'boxed' vs classic 'default').
const isBoxed = computed(() => props.variant === 'boxed');

const arrowBtnClass = computed(() =>
    isBoxed.value
        ? 'h-8 w-8 flex items-center justify-center rounded-md text-fg-secondary hover:bg-bg-base disabled:!bg-transparent disabled:!text-fg-tertiary disabled:!opacity-50 transition-colors focus:outline-0'
        : 'h-9 flex py-2 px-1.5 rounded items-center justify-center min-w-9 leading-tight border-2 border-transparent disabled:!text-slate-800 dark:disabled:!text-slate-500 disabled:!bg-transparent disabled:!opacity-50 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-700 focus:border-slate-300 dark:focus:border-slate-600 focus:outline-0'
);

const pageBtnBaseClass = computed(() =>
    isBoxed.value
        ? 'h-8 min-w-8 px-1.5 flex items-center justify-center rounded-md body-md-500 transition-colors focus:outline-0'
        : 'h-9 flex p-2 rounded items-center justify-center min-w-9 leading-tight border-2 border-transparent focus:z-20 focus:border-slate-300 dark:focus:border-slate-600 focus:outline-0 active:border-transparent'
);

const pageBtnStateClass = (isCurrent) => {
    if (isBoxed.value) {
        return isCurrent
            ? 'bg-bg-intense text-fg-inverted'
            : 'text-fg-secondary hover:bg-bg-base';
    }

    return isCurrent
        ? 'text-orange-500 bg-gray-100 dark:bg-slate-700 font-medium'
        : 'text-slate-800 dark:text-gray-100 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-700';
};

const ellipsisClass = computed(() =>
    isBoxed.value
        ? 'h-8 min-w-8 flex items-center justify-center body-md text-fg-tertiary select-none'
        : 'h-9 flex p-2 items-center justify-center min-w-9 leading-tight text-slate-400 select-none'
);

const countPClass = computed(() =>
    isBoxed.value
        ? 'text-fg-tertiary text-sm font-normal leading-tight'
        : 'text-slate-500 text-sm font-normal leading-tight'
);

const startPage = computed(() => {
    // Ensure we never go below page 1
    const minPage = 1;
    const maxPage = Math.max(1, props.totalPages);

    if (props.currentPage === minPage) {
        return minPage;
    }

    // Calculate the start page based on current page and max visible buttons
    let start = props.currentPage - Math.floor(props.maxVisibleButtons / 2);

    // Adjust if we're near the end
    if (start + props.maxVisibleButtons > maxPage) {
        start = Math.max(minPage, maxPage - props.maxVisibleButtons + 1);
    }

    // Ensure we never go below page 1
    return Math.max(minPage, start);
});

const pages = computed(() => {
    const range = [];
    const maxPage = Math.max(1, props.totalPages);

    for (
        let i = startPage.value;
        i <= Math.min(startPage.value + props.maxVisibleButtons - 1, maxPage);
        i++
    ) {
        range.push({
            name: i,
            isDisabled: i === props.currentPage,
        });
    }
    return range;
});

// Edge-page helpers (only meaningful when showEdges is true).
const lastPageNumber = computed(() => Math.max(1, props.totalPages));
const showLeadingEdge = computed(
    () => props.showEdges && startPage.value > 1
);
const showTrailingEdge = computed(() => {
    const endPage = startPage.value + props.maxVisibleButtons - 1;
    return props.showEdges && endPage < lastPageNumber.value;
});

// Narrow page window for the mobile boxed pager (so "‹ 1 … 8 9 10 … 17 ›"
// fits a 375px viewport). Always shows the first/last edge when out of range.
const mobileWindow = computed(() => {
    const maxVisible = 3;
    const maxPage = Math.max(1, props.totalPages);
    let start = props.currentPage - Math.floor(maxVisible / 2);
    if (start + maxVisible > maxPage) {
        start = maxPage - maxVisible + 1;
    }
    start = Math.max(1, start);
    const pages = [];
    for (let i = start; i <= Math.min(start + maxVisible - 1, maxPage); i++) {
        pages.push({ name: i });
    }
    const end = Math.min(start + maxVisible - 1, maxPage);
    return {
        pages,
        showFirst: start > 1,
        showLeadingEllipsis: start > 2,
        showLast: end < maxPage,
        showTrailingEllipsis: end < maxPage - 1,
    };
});

const startRange = computed(() => {
    return (props.currentPage - 1) * props.perPage + 1;
});

const endRange = computed(() => {
    const end = props.currentPage * props.perPage;
    return end > props.totalResults ? props.totalResults : end;
});

const isInFirstPage = computed(() => props.currentPage === 1);
const isInLastPage = computed(() => props.currentPage === props.totalPages);

const prev = () => {
    emit('pageChanged', props.currentPage - 1);
};

const goToPage = (page) => {
    emit('pageChanged', page);
};

const next = () => {
    emit('pageChanged', props.currentPage + 1);
};

// TODO: if we ever want 'skip to last page'/'back to first' buttons
const onClickLastPage = () => {
    emit('pageChanged', props.totalPages);
};
const onClickFirstPage = () => {
    emit('pageChanged', 1);
};
</script>
